import React, { useState, useRef, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWhitelabel } from '../context/WhitelabelContext';
import api, { handleCatch } from '../api';

const RESEND_OTP_TIME_LIMIT = 30;

const OtpVerificationScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mobile, secretKey } = location.state || {};
  const { config: whitelabelConfig } = useWhitelabel();

  const [otpArray, setOtpArray] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendButtonDisabledTime, setResendButtonDisabledTime] = useState(RESEND_OTP_TIME_LIMIT);

  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const primaryColor = whitelabelConfig?.primaryColor || '#0066CC';
  const secondaryColor = whitelabelConfig?.secondaryColor || '#004C99';

  useEffect(() => {
    if (!mobile || !secretKey) {
      navigate('/forgot-password');
      return;
    }
    if (inputRefs[0].current) inputRefs[0].current.focus();
    const interval = setInterval(() => {
      setResendButtonDisabledTime((prev) => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [mobile, secretKey, navigate]);

  const onOtpChange = (index) => (event) => {
    const value = event.target.value;
    if (isNaN(Number(value))) return;

    if (value.length > 1) {
      const chars = value.split('').slice(0, 4);
      const newArr = [...otpArray];
      chars.forEach((char, i) => { if (index + i < 4) newArr[index + i] = char; });
      setOtpArray(newArr);
      const lastIndex = Math.min(index + chars.length - 1, 3);
      setTimeout(() => { if (inputRefs[lastIndex].current) inputRefs[lastIndex].current.focus(); }, 10);
      return;
    }

    const newArr = [...otpArray];
    newArr[index] = value;
    setOtpArray(newArr);
    if (value !== '' && index < 3) {
      setTimeout(() => { if (inputRefs[index + 1].current) inputRefs[index + 1].current.focus(); }, 10);
    }
    setError('');
  };

  const onOtpKeyDown = (index) => (event) => {
    if (event.key === 'Backspace' && otpArray[index] === '' && index > 0) {
      setTimeout(() => { if (inputRefs[index - 1].current) inputRefs[index - 1].current.focus(); }, 10);
    }
  };

  const onResendOtp = async () => {
    setOtpArray(['', '', '', '']);
    if (inputRefs[0].current) inputRefs[0].current.focus();
    setResendButtonDisabledTime(RESEND_OTP_TIME_LIMIT);
    setLoading(true);
    try {
      const response = await api.post('api/TOKEN/forget-password-client-otp', { mobile });
      navigate('/verify-otp', { replace: true, state: { mobile, secretKey: response.data } });
    } catch (error) {
      const errorMsg = handleCatch(error);
      setError(errorMsg || 'Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    for (let i = 0; i < 4; i++) {
      if (otpArray[i].length === 0) {
        setError('Please enter complete OTP');
        if (inputRefs[i].current) inputRefs[i].current.focus();
        return;
      }
    }
    setLoading(true);
    setError('');
    try {
      const response = await api.post('api/TOKEN/verify-client-otp', {
        mobile, secretKey, otp: otpArray.join(''),
      });
      navigate('/reset-password', { state: { mobile, userName: response.data } });
    } catch (error) {
      const errorMsg = handleCatch(error);
      setError(errorMsg || 'Invalid OTP. Please try again.');
      setOtpArray(['', '', '', '']);
      if (inputRefs[0].current) inputRefs[0].current.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: `linear-gradient(160deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
      }}
    >
      {/* Top bar with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, pt: 2, pb: 1 }}>
        <IconButton
          onClick={() => navigate('/forgot-password')}
          sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', width: 40, height: 40 }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>

      {/* Title section */}
      <Box sx={{ px: 3, pb: 3, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 26, fontWeight: 'bold', color: '#fff', mb: 0.5 }}>
          Enter OTP
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.85)' }}>
          We've sent a verification code to
        </Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 'bold', color: '#fff', mt: 0.3 }}>
          +91 {mobile}
        </Typography>
      </Box>

      {/* White card */}
      <Box
        sx={{
          flex: 1,
          bgcolor: '#fff',
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
          px: 3,
          pt: 3,
          pb: 4,
          overflowY: 'auto',
        }}
      >
        <Typography sx={{ fontWeight: 'bold', fontSize: 18, color: '#333', textAlign: 'center', mb: 0.5 }}>
          Verify OTP
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#888', textAlign: 'center', mb: 3 }}>
          Enter the 4-digit code sent to your mobile
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontSize: 13 }}>
            {error}
          </Alert>
        ) : null}

        <Box component="form" onSubmit={onSubmit}>
          {/* OTP boxes */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mb: 3 }}>
            {otpArray.map((digit, index) => (
              <TextField
                key={index}
                inputRef={inputRefs[index]}
                value={digit}
                onChange={onOtpChange(index)}
                onKeyDown={onOtpKeyDown(index)}
                inputProps={{
                  maxLength: 1,
                  style: { textAlign: 'center', fontSize: 24, fontWeight: 'bold', padding: '10px 0' },
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                sx={{
                  width: 60,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '& fieldset': { borderColor: primaryColor, borderWidth: 2 },
                    '&:hover fieldset': { borderColor: primaryColor },
                    '&.Mui-focused fieldset': { borderColor: primaryColor },
                  },
                }}
              />
            ))}
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || otpArray.some((d) => d === '')}
            sx={{
              height: 50,
              borderRadius: 25,
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: 15,
              bgcolor: primaryColor,
              mb: 2,
              '&:hover': { bgcolor: secondaryColor },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Continue'}
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            {resendButtonDisabledTime > 0 ? (
              <Typography sx={{ fontSize: 13, color: '#888' }}>
                Resend OTP in {resendButtonDisabledTime}s
              </Typography>
            ) : (
              <Button
                onClick={onResendOtp}
                disabled={loading}
                startIcon={<RefreshIcon />}
                sx={{ color: primaryColor, textTransform: 'none', fontWeight: 600, fontSize: 13 }}
              >
                Resend OTP
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OtpVerificationScreen;
